<template>
  <div class="authWrapper new-login-wrapper">
    <div class="container">
      <div class="row whiteBG-wrap login-white-wrap">
        <div class="col-lg-7 col-md-7 p-0">
          <div class="login-left-wrap login-otp-left-wrap">
            <!-- Back button -->
            <div class="back-icon text-start">
              <router-link to="/login-password" class="ms-0 pl-0 d-inline-block">
                Back
              </router-link>
            </div>

            <!-- Logo -->
            <div class="left-login">
              <div class="rep-icon-login">
                <img
                  src="../../assets/images/member-login.svg"
                  alt="image-fluid"
                />
              </div>
            </div>

            <!-- OTP Form -->
            <h4 class="login-title mb-1">Login to Continue</h4>
            <div class="auth-form pt-3 pb-3">
              <div class="email-change-wrap mb-3">
                <div>{{ email }}</div>
                <div
                  class="login-highlight d-inline-block"
                  role="button"
                  tabindex="0"
                  @click="routeToLogin"
                  @keydown.enter="routeToLogin"
                  aria-label="Change email address"
                >
                  Change
                </div>
              </div>

              <form
                class="new-login-otp-form"
                @submit.prevent="verifyToken"
                @keydown.enter="isLockedOut ? $event.preventDefault() : null"
              >
                <div class="mt-3">
                  <div class="d-flex">
                    <label class="opt-label mt-0">
                      Enter OTP<span class="required-sign"> *</span>
                    </label>
                  </div>

                  <!-- OTP Inputs -->
                  <div class="d-flex flex-column">
                    <div class="otp-input-wrapper d-flex">
                      <input
                        v-for="(digit, index) in digits"
                        :key="index"
                        ref="inputs"
                        v-model="digits[index]"
                        type="tel"
                        maxlength="1"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        class="otp-input"
                        :class="{ 'error-border': otpErrorFlag && !isLockedOut }"
                        :disabled="isLockedOut"
                        @input="handleInput(index, $event)"
                        @keydown.delete="handleDelete(index, $event)"
                        @paste.prevent="handlePaste"
                        @focus="clearError"
                        :aria-label="`OTP digit ${index + 1}`"
                        :aria-describedby="otpErrorFlag && !isLockedOut ? 'otp-error' : null"
                      >
                    </div>
                    <div
                      v-if="otpErrorFlag && !isLockedOut"
                      class="valid-error mt-2"
                      aria-live="polite"
                      id="otp-error"
                    >
                      {{ errorMsg }}
                    </div>
                  </div>
                </div>

                <!-- Submit Button -->
                <button
                  :disabled="!isOtpComplete || formLoader || isLockedOut"
                  class="submit-btn mt-3 next-btn d-flex align-items-center justify-content-center"
                  type="submit"
                >
                  <b-spinner v-if="formLoader" small class="me-2" />
                  <span>{{ formLoader ? 'Logging in...' : 'Login' }}</span>
                </button>
              </form>

              <!-- OTP Resend Component or Lockout Timer -->
              <div class="resend-timer-wrap">
                <login-otp
                  v-if="!isLockedOut"
                  @sendOTP="sendOTP"
                  :expiry-second="60"
                  :is-loading="otpLoader"
                />
                <lockout-timer
                  v-if="isLockedOut && lockoutSeconds > 0"
                  :lockout-seconds="lockoutSeconds"
                  @lockout-expired="handleLockoutExpired"
                />
              </div>
            </div>
          </div>
        </div>
        
        <!-- Carousel Section -->
        <div class="col-lg-5 col-md-5 p-0">
          <login-in-carsoule />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Api from "@/resource/MemberApi";
import NewLoginOtp from "./NewLoginOtp.vue";
import helper from "@/resource/Helper";
import store from "../../store/store";
import Logincarsoule from "./Logincarsoule";
import LockoutTimer from "./OtpLockoutTimer.vue";
import { debounce } from "lodash";

export default {
  name: "LoginOtp",
  components: {
    "login-in-carsoule": Logincarsoule,
    "login-otp": NewLoginOtp,
    "lockout-timer": LockoutTimer
  },
  data() {
    return {
      digits: Array(6).fill(''),
      otpLoader: false,
      formLoader: false,
      otpErrorFlag: false,
      errorMsg: "",
      isLockedOut: false,
      lockoutSeconds: 0,
      isResend: false
    };
  },
  computed: {
    ...mapGetters({
      deviceId: "deviceId",
      email: "memberEmail"
    }),
    isOtpComplete() {
      return this.digits.every(d => d.length === 1 && /\d/.test(d));
    },
    otp() {
      return this.digits.join('');
    }
  },
  mounted() {
    this.focusFirstInput();
    this.debouncedVerifyToken = debounce(this.verifyToken, 300);
  },
  methods: {
    routeToLogin() {
      this.$router.push("/");
    },
    handleInput(index, event) {
      if (this.isLockedOut) return;
      const value = event.target.value.replace(/\D/g, '');
      this.$set(this.digits, index, value.slice(-1));
      
      if (value && index < 5) {
        this.$refs.inputs[index + 1].focus();
      }
      
      if (this.isOtpComplete) {
        this.debouncedVerifyToken();
      }
    },
    handleDelete(index) {
      if (this.isLockedOut) return;
      if (!this.digits[index] && index > 0) {
        this.$refs.inputs[index - 1].focus();
      }
    },
    handlePaste(event) {
      if (this.isLockedOut) return;
      const pasteData = (event.clipboardData || window.clipboardData)
        .getData('text/plain')
        .replace(/\D/g, '')
        .slice(0, 6)
        .split('');

      this.digits = [...pasteData, ...Array(6 - pasteData.length).fill('')];
      this.focusInput(pasteData.length);
      
      if (this.isOtpComplete) {
        this.debouncedVerifyToken();
      }
    },
    focusFirstInput(retry = 5) {
      this.$nextTick(() => {
        const input = this.$refs.inputs?.[0];
        if (input && !this.isLockedOut) {
          input.focus();
        } else if (retry > 0) {
          setTimeout(() => this.focusFirstInput(retry - 1), 50);
        }
      });
    },
    focusInput(index) {
      if (this.isLockedOut) return;
      this.$nextTick(() => {
        const targetIndex = Math.min(index, 5);
        this.$refs.inputs[targetIndex]?.focus();
      });
    },
    clearError() {
      if (this.isLockedOut) return;
      this.otpErrorFlag = false;
      this.errorMsg = '';
    },
    parseLockoutDuration(message) {
      console.log('Parsing lockout duration:', message);
      if (!message) {
        console.warn('No message provided, using default duration of 60 seconds');
        return 60;
      }
      const secondsMatch = message.match(/Try again after (\d+) seconds/);
      if (secondsMatch && secondsMatch[1]) {
        const parsedSeconds = parseInt(secondsMatch[1], 10);
        console.log('Parsed as seconds:', parsedSeconds);
        return parsedSeconds;
      }
      const match = message.match(/(\d+)\s*minute[s]?(\s*and\s*(\d+)\s*second[s]?)?|(\d+)\s*second[s]?/);
      if (!match) {
        console.warn('Failed to parse lockout duration, using default of 60 seconds:', message);
        return 60;
      }
      const minutes = parseInt(match[1]) || 0;
      const seconds = parseInt(match[3]) || parseInt(match[4]) || 0;
      const totalSeconds = minutes * 60 + seconds;
      console.log('Parsed duration:', { minutes, seconds, totalSeconds });
      return totalSeconds;
    },
    async verifyToken() {
      if (!this.isOtpComplete || this.formLoader) {
        this.otpErrorFlag = true;
        this.errorMsg = "Please enter a 6-digit OTP to proceed";
        this.focusInput(0);
        return;
      }

      this.formLoader = true;
      this.clearError();

      if (!/^\d{6}$/.test(this.otp)) {
        this.otpErrorFlag = true;
        this.errorMsg = "OTP must be 6 digits";
        this.formLoader = false;
        this.focusInput(0);
        return;
      }

      try {
        const response = await Api.post(
          `v4/auth/validate-otp`,
          {
            email: this.email,
            device_id: this.deviceId,
            verification_code: this.otp,
            user_type: "M"
          }
        );

        if (response.data.status === "success") {
          store.commit("setAuthentication", true);
          helper.setLoginDetails(response.data);
        }
      } catch (error) {
        const errorResponse = error.response?.data;
        if (errorResponse?.status === 429 || error.response?.status === 429) {
          this.isLockedOut = true;
          this.lockoutSeconds = this.parseLockoutDuration(errorResponse?.message || '60 seconds');
          this.otpErrorFlag = false;
          this.errorMsg = '';
          console.log('Lockout activated:', { isLockedOut: this.isLockedOut, lockoutSeconds: this.lockoutSeconds });
        } else {
          this.handleValidationError(error);
        }
      } finally {
        this.formLoader = false;
      }
    },
    handleValidationError(error) {
      this.otpErrorFlag = true;
      this.errorMsg = error.response?.data?.message || 'Invalid OTP. Please try again.';
      this.focusInput(this.digits.findIndex(d => !d) !== -1 ? this.digits.findIndex(d => !d) : 5);
    },
    async sendOTP() {
      if (this.isLockedOut) return;
      this.otpLoader = true;
      try {
        const response = await Api.post(
          `v4/auth/generate-otp`,
          {
            email: this.email,
            device_id: this.deviceId,
            type: 'email',
            user_type: "M",
            is_resend: this.isResend
          }
        );

        if (response.data.status === "success") {
          helper.infoMessage(this, 'success', response.data.message);
          this.digits = Array(6).fill('');
          this.clearError();
          this.focusFirstInput();
          this.isResend = true;
        }
      } catch (error) {
        const errorResponse = error.response?.data;
        if (errorResponse?.status === 429 || error.response?.status === 429) {
          this.isLockedOut = true;
          this.lockoutSeconds = this.parseLockoutDuration(errorResponse?.message || '60 seconds');
          this.otpErrorFlag = false;
          this.errorMsg = '';
          console.log('Lockout activated:', { isLockedOut: this.isLockedOut, lockoutSeconds: this.lockoutSeconds });
        } else {
          helper.infoMessage(
            this,
            'error',
            errorResponse?.message || 'Failed to send OTP'
          );
        }
      } finally {
        this.otpLoader = false;
      }
    },
    handleLockoutExpired() {
      console.log('Lockout expired at:', new Date().toISOString());
      this.isLockedOut = false;
      this.lockoutSeconds = 0;
      this.otpErrorFlag = false;
      this.errorMsg = '';
      this.digits = Array(6).fill('');
      this.focusFirstInput();
    }
  }
};
</script>

<style src="@/assets/css/authen.css"></style>
<style src="@/assets/css/newLogin.css"></style>

<style>
.otp-input-wrapper {
  gap: 25px;
  margin: 10px 0;
}
.auth-form .otp-input-wrapper input.otp-input {
  height: 53px;
  border: 1px solid #bbb;
  box-shadow: 0;
  border-radius: 8px;
  padding: 10px !important;
}
.auth-form .otp-input-wrapper input.otp-input::after {
  content: '-';
  position: absolute;
  right: -5px;
}
.otp-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  outline: none;
}

#label-username,
#label-password {
  font-size: 16px !important;
  color: #909090 !important;
  margin-bottom: 10px !important;
}
#username,
#password {
  font-size: 14px !important;
  padding: 12px 15px !important;
}
.rep-icon-login {
  width: 80px;
}

.new-login-wrapper .highlight_otp {
  border: 2px solid #dc3545 !important;
}

.otp-input.error-border {
  border-color: #dc3545;
}

.otp-separator {
  margin: 0 4px;
  font-weight: bold;
  font-size: 20px;
  color: #333333;
}

.submit-btn {
  display: block;
  width: 260px;
  margin: 1rem 0;
  padding: 8px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background-color: #0056b3;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.resend-timer-wrap {
  margin: 0 auto;
  justify-content: left !important;
  align-items: left !important;
}
</style>

<style scoped>
.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
